import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { isCustomer, isValidObject } from "app/main/apps/common/AuraFunctions";
import { SimpleCardDocumentsViewer } from "app/main/apps/common/ImageRenderer";
import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import NoItemMessage from "app/main/apps/common/NoItemMessage";
import { AuraSimpleTableHead } from "app/main/apps/common/AuraSimpleTable";
import {
  PreviewSectionTitle,
  PreviewSectionSubTitle,
  PreviewFieldHeader,
  PreviewFieldText,
  PreviewFieldArea,
  PreviewDocumentArea,
} from "./VasStyledComponent";
import { vasProductHeader } from "./VasHelper";

const VasPreview = (props) => {
  const {
    dialogButtons,
    data: vasData,
    completeOrderData,
    orderdetails,
  } = props;
  const currentUser = useSelector(({ user }) => user);
  const [isPrinting, setIsPrinting] = useState(false);
  const showOrderNo = props.showOrderNo ?? true;
  const showProductSection = props.showProductSection ?? true;
  const showProofDocuments = props.showProofDocuments ?? true;
  const showProductionDocuments = props.showProductionDocuments ?? true;
  const showProductProofDocuments = props.showProductProofDocuments ?? true;
  const labelRef = useRef();
  const promiseResolveRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const vasProducts = orderdetails || completeOrderData?.order_details;

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  // PGSS-5148: react-to-print v3 (contentRef) — v2 used findDOMNode, removed in React 19.
  const handleLabelDownload = useReactToPrint({
    contentRef: labelRef,
    onBeforePrint: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
        setLoading(true);
      });
    },
    onAfterPrint: () => {
      // Reset the Promise resolve so we can print again
      promiseResolveRef.current = null;
      setIsPrinting(false);
      setLoading(false);
    },
    pageStyle: `
              @media print {
                  body {
                      padding: 20px;
                      font-size: 12px;
                      background-color: white !important;
                      -webkit-print-color-adjust: exact;
                    }
                  .scale-content {
                    transform: scale(0.6); /* Scale content to 60% */
                    transform-origin: top left; /* Set transformation origin */
                  }
                  @page {
                    padding-top: 20px !important; /* Add space for header */
                  }
                  .element-with-spacing,
                  .print-card,
                  .label-container {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                  }
                  .label-container {
                    page-break-after: always;
                  }
                }
              `,
    documentTitle: completeOrderData?.order_no
      ? `Order_${completeOrderData?.order_no}_VAS.pdf`
      : "VAS.pdf",
  });

  const isValidVasData =
    (vasData?.sandblasting && isValidObject(vasData?.sandblasting)) ||
    (vasData?.etching && isValidObject(vasData?.etching));

  const FieldContent = ({ data, title, fieldName }) => {
    return data[fieldName] ? (
      <PreviewFieldArea>
        <PreviewFieldHeader>{title}</PreviewFieldHeader>
        <PreviewFieldText>{data[fieldName]}</PreviewFieldText>
      </PreviewFieldArea>
    ) : (
      <></>
    );
  };

  const SimpleFieldContent = ({
    data,
    title,
    fieldName,
    yesNoField = false,
  }) => {
    return (
      <PreviewFieldArea
        className="flex flex-row"
        style={{ marginBottom: "1.2rem" }}
      >
        <PreviewFieldHeader className="mr-16">{title}</PreviewFieldHeader>
        {yesNoField === true ? (
          <PreviewFieldText>
            {data[fieldName] === true || data[fieldName] === "true"
              ? "YES"
              : "NO"}
          </PreviewFieldText>
        ) : (
          <></>
        )}
        {yesNoField === false && (
          <PreviewFieldText>{data[fieldName]}</PreviewFieldText>
        )}
      </PreviewFieldArea>
    );
  };

  const DocumentContent = ({ data }) => {
    return (
      <PreviewFieldArea>
        <PreviewFieldHeader>
          Design of Sketch or Photo Upload
        </PreviewFieldHeader>
        <PreviewDocumentArea>
          <SimpleCardDocumentsViewer
            attachments={data.documents}
            isPrinting={isPrinting}
          />
        </PreviewDocumentArea>
      </PreviewFieldArea>
    );
  };

  const SectionContent = ({ data }) => {
    if (!data) return null;
    return (
      <>
        <FieldContent
          data={data}
          title="Design Instruction"
          fieldName="design_instruction"
        />
        <FieldContent
          data={data}
          title="Special instruction - Note"
          fieldName="special_instruction_note"
        />
        <FieldContent data={data} title="Epitaph" fieldName="epitaph" />
        <FieldContent data={data} title="Lettering" fieldName="lettering" />
        <FieldContent
          data={data}
          title="Front Lettering"
          fieldName="front_lettering"
        />
        <FieldContent
          data={data}
          title="Back Lettering"
          fieldName="back_lettering"
        />
        {data.documents?.length > 0 && <DocumentContent data={data} />}
      </>
    );
  };

  const PorcelainSectionContent = ({ data }) => {
    if (!data) return null;
    return (
      <>
        <SimpleFieldContent
          data={data}
          title="Customer Privided"
          fieldName="customer_provided"
          yesNoField
        />
        {data.color && (
          <SimpleFieldContent data={data} title="Color" fieldName="color" />
        )}
        {data.shape && (
          <SimpleFieldContent data={data} title="Shape" fieldName="shape" />
        )}
        {data.size && (
          <SimpleFieldContent data={data} title="Size" fieldName="size" />
        )}
        <SimpleFieldContent
          data={data}
          title="Recess"
          fieldName="recess"
          yesNoField
        />
        {data.recess === true && data.recess_depth && (
          <SimpleFieldContent
            data={data}
            title="Recess Depth"
            fieldName="recess_depth"
          />
        )}
        <SimpleFieldContent
          data={data}
          title="Install"
          fieldName="install"
          yesNoField
        />
        {data.documents?.length > 0 && <DocumentContent data={data} />}
      </>
    );
  };

  const Section = ({ title, data, type = "default" }) => {
    if (!data) return null;
    return (
      <div className={`${isPrinting ? "print-card mb-20" : "mb-20"}`}>
        <div className="bg-[#f4f6ff] border-1 border-[#4f3ff0] rounded-md">
          <div
            className={`text-white px-16 py-6 rounded-t-md ${
              type === "proof_documents" ||
              type === "production_documents" ||
              type === "product_proof_documents"
                ? "bg-[#36A247]"
                : "bg-[#4f3ff0] "
            }`}
          >
            <PreviewSectionTitle>{title}</PreviewSectionTitle>
          </div>
          <div className="px-16 pt-10 rounded-b-md">
            {type === "default" && <SectionContent data={data} />}
            {type === "front_back" && (
              <>
                {data?.front && (
                  <>
                    <PreviewSectionSubTitle>Front Side</PreviewSectionSubTitle>
                    <SectionContent data={data?.front} />
                  </>
                )}
                {isValidObject(data.front) && (
                  <Divider className="my-16 border-1" />
                )}
                {data?.back && (
                  <>
                    <PreviewSectionSubTitle>Back Side</PreviewSectionSubTitle>
                    <SectionContent data={data?.back} />
                  </>
                )}
                {data.documents?.length > 0 && <DocumentContent data={data} />}
              </>
            )}
            {type === "both" && (
              <>
                {data.product_type && (
                  <PreviewFieldArea>
                    <PreviewFieldHeader>Product Type</PreviewFieldHeader>
                    <PreviewFieldText>{data.product_type}</PreviewFieldText>
                  </PreviewFieldArea>
                )}
                {isValidObject(data?.front) && (
                  <>
                    <PreviewSectionSubTitle>Front Side</PreviewSectionSubTitle>
                    <SectionContent data={data?.front} />
                  </>
                )}
                {isValidObject(data?.back) && (
                  <>
                    <PreviewSectionSubTitle>Back Side</PreviewSectionSubTitle>
                    <SectionContent data={data?.back} />
                  </>
                )}
                {isValidObject(data?.single) && (
                  <SectionContent data={data?.single} />
                )}
                {data.documents?.length > 0 && <DocumentContent data={data} />}
              </>
            )}
            {type === "porcelain" && <PorcelainSectionContent data={data} />}
            {type === "lettering" && <LetteringSection data={data} />}
            {type === "blasting" && <BlastingSection data={data} />}
            {type === "etching" && <EtchingSection data={data} />}
            {type === "proof_documents" && (
              <PreviewFieldArea>
                <SimpleCardDocumentsViewer
                  attachments={data.proof_documents}
                  isPrinting={isPrinting}
                  showSupersededBadge
                />
              </PreviewFieldArea>
            )}
            {type === "production_documents" && (
              <PreviewFieldArea>
                <SimpleCardDocumentsViewer
                  attachments={data.production_documents}
                  isPrinting={isPrinting}
                  showSupersededBadge
                />
              </PreviewFieldArea>
            )}
            {type === "product_proof_documents" && (
              <PreviewFieldArea>
                <SimpleCardDocumentsViewer
                  attachments={data.product_proof_documents}
                  isPrinting={isPrinting}
                  showSupersededBadge
                />
              </PreviewFieldArea>
            )}
          </div>
        </div>
      </div>
    );
  };

  const LetteringSection = ({ data }) => {
    if (!data || data.length === 0) return null;
    return (
      <>
        <div className="mb-6 flex flex-row">
          <PreviewFieldHeader className="w-1/3">Name</PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/3">
            Letter Style
          </PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/3">Litho</PreviewFieldHeader>
        </div>
        <Divider className="my-8 border-1" />
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/3">Sur Name</PreviewFieldText>
          <PreviewFieldText className="w-1/3">
            {data.surname_style}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/3">
            {data.surname_litho}
          </PreviewFieldText>
        </div>
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/3">
            Rest of the stone
          </PreviewFieldText>
          <PreviewFieldText className="w-1/3">
            {data.rest_of_stone_style}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/3">
            {data.rest_of_stone_litho}
          </PreviewFieldText>
        </div>
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/3">Back of stone</PreviewFieldText>
          <PreviewFieldText className="w-1/3">
            {data.back_of_stone_style}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/3">
            {data.back_of_stone_litho}
          </PreviewFieldText>
        </div>
        {data.no_of_premium_lettering && (
          <>
            <SimpleFieldContent
              data={data}
              title="No:of Premimum Lettring"
              fieldName="no_of_premium_lettering"
            />
          </>
        )}
        {data.documents?.length > 0 && <DocumentContent data={data} />}
      </>
    );
  };

  const BlastingSection = ({ data }) => {
    if (!data?.elements?.length) return null;

    return (
      <>
        {/* No. of Colors header (only shown when color_sandblast is true) */}
        {data.color_sandblast && (
          <div className="mb-2">
            <PreviewFieldHeader>
              No. of Colors: {data.no_of_colors}
            </PreviewFieldHeader>
          </div>
        )}

        {/* Main table headers */}
        <div className="mb-6 flex flex-row">
          <PreviewFieldHeader className="w-1/6">No.</PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/4">Element Name</PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/4">
            Type of Sandblast
          </PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/4">Litho</PreviewFieldHeader>
        </div>
        <Divider className="my-8 border-1" />

        {/* Table rows */}
        {data.elements.map((element, index) => (
          <div key={index} className="mb-6 flex flex-row">
            <PreviewFieldText className="w-1/6">{index + 1}</PreviewFieldText>
            <PreviewFieldText className="w-1/4">
              {element.comments}
            </PreviewFieldText>
            <PreviewFieldText className="w-1/4">
              {element.type_of_sandblast}
            </PreviewFieldText>
            <PreviewFieldText className="w-1/4">
              {element.litho}
            </PreviewFieldText>
          </div>
        ))}

        {data.documents?.length > 0 && <DocumentContent data={data} />}
      </>
    );
  };

  const EtchingSection = ({ data }) => {
    if (!data || data.length === 0) return null;
      return (
      <>
        <div className="mb-6 flex flex-row">
          <PreviewFieldHeader className="w-1/4">
            Type of Etch
          </PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/4">Size</PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/4">Quantity</PreviewFieldHeader>
          <PreviewFieldHeader className="w-1/4">
            Photo Quality DPI
          </PreviewFieldHeader>
        </div>
        <Divider className="my-8 border-1" />
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/4">Spot</PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.spot_size}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.spot_quantity}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.spot_quality}
          </PreviewFieldText>
        </div>
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/4">Scene</PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.scene_size}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.scene_quantity}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.scene_quality}
          </PreviewFieldText>
        </div>
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/4">Portrait</PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.portrait_size}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.portrait_quantity}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.portrait_quality}
          </PreviewFieldText>
        </div>
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/4">
            Multi Person Portrait
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.multi_person_portrait_size}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.multi_person_portrait_quantity}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.multi_person_portrait_quality}
          </PreviewFieldText>
        </div>
        <div className="mb-6 flex flex-row">
          <PreviewFieldText className="w-1/4">Custom Collage</PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.custom_collage_size}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.custom_collage_quantity}
          </PreviewFieldText>
          <PreviewFieldText className="w-1/4">
            {data.custom_collage_quality}
          </PreviewFieldText>
        </div>
        {data.notes && (
          <>
            <Divider className="my-8 border-1" />
            <FieldContent data={data} title="Notes" fieldName="notes" />
          </>
        )}
        {data.documents?.length > 0 && <Divider className="my-8 border-1" />}
        {data.documents?.length > 0 && <DocumentContent data={data} />}
      </>
    );
  };

  const ProductsSection = () => {
    return (
      <div className="my-16">
        <Card variant="outlined" className="table-responsive w-full rounded-sm">
          <Table
            className="simple w-full min-w-full"
            sx={{
              "&.simple tbody tr td:first-of-type": {
                padding: "5px 8px",
              },
              "&.simple thead tr th:first-of-type": {
                padding: "5px 8px",
              },
              "&.simple thead tr th": {
                padding: "5px 8px",
                backgroundColor: "#f0f3fb",
                borderColor: "#FFF",
                fontWeight: "400",
              },
              "&.simple tbody tr td": {
                borderColor: "#FFF",
                padding: "12px 8px",
              },
            }}
          >
            <AuraSimpleTableHead
              headerList={vasProductHeader}
              classes={{
                cnCell: "font-semibold px-8 py-8 capitalize",
              }}
            />
            <TableBody>
              {vasProducts?.map((item, index) => {
                return (
                  <TableRow key={item.key}>
                    <TableCell className="w-[6rem]">{index + 1}</TableCell>
                    <TableCell className="w-[100rem]">{item.name}</TableCell>
                    <TableCell className="w-[14rem]">
                      {item.subType || item.sub_type}
                    </TableCell>
                    <TableCell className="w-[14rem]">
                      {item.customer_provider_number || item.reference_number}
                    </TableCell>
                    <TableCell className="w-120 capitalize">
                      {item.decedent || item.decedents}
                    </TableCell>
                    <TableCell className="w-120 capitalize">
                      {item.quantity}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>
    );
  };

  return (
    <div className="">
      {isValidVasData ? (
        <div
          ref={labelRef}
          className={` ${isPrinting ? "mb-10" : "mb-36"}`}
          style={{ maxHeight: "60vh !important" }}
        >
          {completeOrderData && completeOrderData?.order_no && showOrderNo && (
            <>
              <div className="flex flex-col md:flex-row mb-16">
                <div className="flex flex-row m-auto">
                  <Typography
                    className="font-medium mr-8 text-20"
                    color="inherit"
                  >
                    Order No:
                  </Typography>
                  <div className="font-bold text-20 opacity-75">
                    {completeOrderData?.order_no || "-"}
                  </div>
                </div>
              </div>
            </>
          )}
          {completeOrderData &&
            vasProducts?.length > 0 &&
            showProductSection && (
              <>
                <ProductsSection />
              </>
            )}
          {vasData?.types?.Ledger &&
            isValidObject(vasData?.sandblasting?.ledger) && (
              <Section title="Ledger" data={vasData?.sandblasting?.ledger} />
            )}
          {vasData?.types?.Marker &&
            isValidObject(vasData?.sandblasting?.marker) && (
              <Section title="Marker" data={vasData?.sandblasting?.marker} />
            )}
          {vasData?.types?.Bevel &&
            isValidObject(vasData?.sandblasting?.bevel) && (
              <Section title="Bevel" data={vasData?.sandblasting?.bevel} />
            )}
          {vasData?.types?.Slant &&
            isValidObject(vasData?.sandblasting?.slant) &&
            (isValidObject(vasData?.sandblasting?.slant?.front) ||
              isValidObject(vasData?.sandblasting?.slant?.back) ||
              vasData?.sandblasting?.slant?.documents?.length > 0) && (
              <Section
                title="Slant"
                data={vasData?.sandblasting?.slant}
                type="front_back"
              />
            )}
          {vasData?.types?.Die &&
            isValidObject(vasData?.sandblasting?.die) &&
            (isValidObject(vasData?.sandblasting?.die?.front) ||
              isValidObject(vasData?.sandblasting?.die?.back) ||
              vasData?.sandblasting?.die?.documents?.length > 0) && (
              <Section
                title="Die"
                data={vasData?.sandblasting?.die}
                type="front_back"
              />
            )}
          {vasData?.types?.Base &&
            isValidObject(vasData?.sandblasting?.base) && (
              <Section title="Base" data={vasData?.sandblasting?.base} />
            )}
          {vasData?.types?.Vase &&
            isValidObject(vasData?.sandblasting?.vase) && (
              <Section title="Vase" data={vasData?.sandblasting?.vase} />
            )}
          {vasData?.types?.Others &&
            isValidObject(vasData?.sandblasting?.others) && (
              <Section
                title="Others"
                data={vasData?.sandblasting?.others}
                type="both"
              />
            )}
          {vasData?.includes?.Porcelain &&
            isValidObject(vasData?.sandblasting?.porcelain) && (
              <Section
                title="Porcelain"
                data={vasData?.sandblasting?.porcelain}
                type="porcelain"
              />
            )}
          {vasData?.services?.sandBlasting === true &&
            vasData?.sandblasting?.lettering &&
            isValidObject(vasData?.sandblasting?.lettering) && (
              <Section
                title="Sandblasting: Lettering"
                data={vasData?.sandblasting?.lettering}
                type="lettering"
              />
            )}
          {vasData?.services?.sandBlasting === true &&
            vasData?.sandblasting?.blasting &&
            isValidObject(vasData?.sandblasting?.blasting) && (
              <Section
                title="Sandblasting: Blasting"
                data={vasData?.sandblasting?.blasting}
                type="blasting"
              />
            )}
          {vasData?.services?.etching === true &&
            vasData?.etching &&
            isValidObject(vasData?.etching) && (
              <Section title="Etching" data={vasData?.etching} type="etching" />
            )}
          {showProofDocuments &&
            completeOrderData?.proof_documents &&
            completeOrderData?.proof_documents.length > 0 && (
              <Section
                title="Proof Documents"
                data={completeOrderData}
                type="proof_documents"
              />
            )}
          {!isCustomer(currentUser.role) &&
            showProductionDocuments &&
            completeOrderData?.production_documents &&
            completeOrderData?.production_documents.length > 0 && (
              <Section
                title="Production Documents"
                data={completeOrderData}
                type="production_documents"
              />
            )}
          {showProductProofDocuments &&
            completeOrderData?.product_proof_documents &&
            completeOrderData?.product_proof_documents.length > 0 && (
              <Section
                title="Product Proof Documents"
                data={completeOrderData}
                type="product_proof_documents"
              />
            )}
        </div>
      ) : (
        <>
          <div className="flex flex-1 items-center justify-center">
            <Box
              sx={{ height: "50vh" }}
              className="flex flex-1 items-center justify-center"
            >
              <NoItemMessage message="No Data Found" />
            </Box>
          </div>
        </>
      )}
      {dialogButtons && (
        <div className="absolute bottom-0 left-0 w-full flex items-center justify-center gap-20 mt-6 bg-[#f6f6f6] py-10">
          {dialogButtons?.showBack ? (
            <Button
              variant="outlined"
              color="secondary"
              className="rounded-md h-[32px] min-h-[32px] max-h-[32px]"
              onClick={props.onBack}
            >
              Back
            </Button>
          ) : (
            <></>
          )}
          {dialogButtons?.showBack ? (
            <Button
              variant="contained"
              color="secondary"
              className="rounded-md h-[32px] min-h-[32px] max-h-[32px]"
              onClick={props.onSave}
              disabled={!isValidVasData}
            >
              Save
            </Button>
          ) : (
            <></>
          )}
          {dialogButtons?.showClose ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                className="rounded-md h-[32px] min-h-[32px] max-h-[32px]"
                onClick={() => handleLabelDownload()}
                disabled={loading}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                className="rounded-md h-[32px] min-h-[32px] max-h-[32px]"
                onClick={props.onClose}
                disabled={loading}
              >
                Close
              </Button>
              {loading && <CircularProgress size="25px" />}
            </>
          ) : (
            <></>
          )}
        </div>
      )}
    </div>
  );
};

export default VasPreview;
